import React, { useEffect, useState } from 'react'
import {
  CustomDropdownItemOption,
  DropDownItemOptionComponentProps,
  SelectDropdown,
  SelectOption,
} from '../../../../../../components/SelectDropdown/SelectDropdown'
import { useGlobalTemplatePresetInputParameters } from '../../hooks'
import { AiFeaturePreset } from '../../../../../../models/AiFeaturePreset'
import { YesNoCancelModal } from '../../../../../../components/Modal/YesNoCancelModal'
import { Button } from '../../../../../../components/Button/Button'
import { TemplateInputParameters } from '../../../../../../models/AiFeature'
import { PresetTemplateConfigForm } from '../PresetTemplateConfigForm'
import { PresetConfigLoader } from '../../Common/PresetConfigLoader'
import { InviteTeamMembers } from '../../Common/InviteTeamMembers'
import { ScopedBlock } from '../../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../../components/ScopedBlock/models'
import { useUserTemplateConfigForm } from './hooks'
import {
  useMeetingTemplateEffectiveInputParameters,
  useInputParametersUpdateScope,
  useTeamMembersFilter,
} from '../../../../../../components/InputParameterHierarchy/hooks'
import {
  InputParameterHierarchyType,
  MeetingTemplateInputParametersHierarchyConfigComponentProps,
} from '../../../../../../components/InputParameterHierarchy/model'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useGetPresetByIdQuery } from '../../../../../../store/ai/ai'

export const TeamMemberPresetTemplateConfigForm: React.FC<MeetingTemplateInputParametersHierarchyConfigComponentProps> = ({
  typeId,
  meetingTemplateId,
  showCustomizationSetting,
  onHide,
  ipHierarchyEnabled,
}) => {
  const accountId = useAppSelector(selectAccountId)

  const { data: presetData } = useGetPresetByIdQuery({ accountId, presetId: typeId })

  const { userFilterValues, selectedUserFilterOption, setSelectedUserFilterOption, teamMembersLoading, hasTeamMembers } = useTeamMembersFilter()

  const { presetInputParameters, presetDefaultInputParameters } = useGlobalTemplatePresetInputParameters(presetData?.data)

  const { inputParameters, parentInputParameters, updateLoading, updateInputParameters, isCustomized } = useMeetingTemplateEffectiveInputParameters(
    InputParameterHierarchyType.AI_FEATURE_PRESET,
    typeId,
    presetInputParameters,
    selectedUserFilterOption?.id,
    meetingTemplateId,
  )

  if (!presetData || !presetData.data || teamMembersLoading || !inputParameters || !presetDefaultInputParameters || !parentInputParameters) {
    return <PresetConfigLoader />
  }

  if (!hasTeamMembers) {
    return <InviteTeamMembers />
  }

  const { example_1 = '', example_2 = '', template } = inputParameters
  const { example_1: defaultExample_1 = '', example_2: defaultExample_2 = '', template: defaultTemplate } = presetDefaultInputParameters
  const { example_1: parentExample_1 = '', example_2: parentExample_2 = '', template: parentTemplate } = parentInputParameters

  return (
    <TeamMemberTemplatePresetConfigFormBody
      preset={presetData.data}
      inputParameters={{ example_1, example_2, template }}
      defaultInputParameters={{ example_1: defaultExample_1, example_2: defaultExample_2, template: defaultTemplate }}
      parentInputParameters={{ example_1: parentExample_1, example_2: parentExample_2, template: parentTemplate }}
      isCustomized={isCustomized}
      showCustomizationSetting={showCustomizationSetting}
      onSave={updateInputParameters}
      onHide={onHide}
      updateLoading={updateLoading}
      userFilterValues={userFilterValues}
      selectedUserFilterOption={selectedUserFilterOption}
      setSelectedUserFilterOption={setSelectedUserFilterOption}
      ipHierarchyEnabled={ipHierarchyEnabled}
    />
  )
}

interface TeamMemberTemplatePresetConfigFormBodyProps {
  preset: AiFeaturePreset
  inputParameters: TemplateInputParameters
  parentInputParameters: TemplateInputParameters
  defaultInputParameters: TemplateInputParameters
  showCustomizationSetting: boolean
  isCustomized: boolean
  updateLoading: boolean
  onSave: (inputParameters: TemplateInputParameters, isCustomized: boolean) => Promise<void>
  onHide: () => void
  userFilterValues: SelectOption[]
  selectedUserFilterOption: SelectOption | null
  setSelectedUserFilterOption: (newSelectedOption: SelectOption) => void
  ipHierarchyEnabled: boolean
}

const TeamMemberTemplatePresetConfigFormBody: React.FC<TeamMemberTemplatePresetConfigFormBodyProps> = ({
  preset,
  inputParameters,
  parentInputParameters,
  defaultInputParameters,
  showCustomizationSetting,
  isCustomized,
  updateLoading,
  onSave,
  onHide,
  userFilterValues,
  selectedUserFilterOption,
  setSelectedUserFilterOption,
  ipHierarchyEnabled,
}) => {
  const { hasIpHierarchyUpdateScope } = useInputParametersUpdateScope(selectedUserFilterOption?.id)
  const disabled = !ipHierarchyEnabled || !hasIpHierarchyUpdateScope

  const [changeToUser, setChangeToUser] = useState<SelectOption | null>(null)

  const {
    editedInputParameters,
    isChanged,
    customized,
    showConfirmation,
    shouldReset,
    isGenerating,
    showGenerate,
    setEditedInputParameters,
    setShouldReset,
    setShowConfirmation,
    setShowGenerate,
    setGenerating,
    handleChangeCustomized,
    handleChangeExample,
    handleChangeTemplate,
    handleClickBack,
    handleClickCancel,
    handleClickSave,
    checkForChange,
  } = useUserTemplateConfigForm(inputParameters, parentInputParameters, isCustomized, onSave, onHide, disabled)

  const handleUserSelected = (selectOption: SelectOption) => {
    if (isChanged && !disabled) {
      setShowConfirmation(true)
      setChangeToUser(selectOption)
    } else {
      setSelectedUserFilterOption(selectOption)
    }
    return !isChanged
  }

  useEffect(() => {
    if (!selectedUserFilterOption) {
      return
    }
    checkForChange()
    setShouldReset(false)
  }, [checkForChange, selectedUserFilterOption])

  const TeamMemberDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = ({ onClick, optionId, optionLabel }) => {
    const selectOption = { id: optionId, label: optionLabel }
    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      const shouldProceed = handleUserSelected(selectOption)
      if (shouldProceed) {
        onClick(e)
      }
    }
    return (
      <CustomDropdownItemOption role='button' onClick={handleClick}>
        {optionLabel}
      </CustomDropdownItemOption>
    )
  }

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => {
          setShowConfirmation(false)
          setChangeToUser(null)
        }}
        message={'Do you want to save your changes?'}
        onYes={async () => {
          await onSave(editedInputParameters, customized)
          if (changeToUser) {
            setSelectedUserFilterOption(changeToUser)
            setChangeToUser(null)
            setShowConfirmation(false)
          } else {
            onHide()
          }
        }}
        onNo={() => {
          setEditedInputParameters(inputParameters)
          if (changeToUser) {
            setSelectedUserFilterOption(changeToUser)
            setChangeToUser(null)
            setShowConfirmation(false)
          } else {
            onHide()
          }
        }}
      />
      <div className='d-flex flex-column gap-4'>
        <div>
          <div className='mb-1 paragraph-medium-extended'>Choose team member</div>
          <SelectDropdown
            placeholder='Select a team member'
            options={userFilterValues}
            onOptionSelected={handleUserSelected}
            initialSelectedOption={selectedUserFilterOption}
            searchable
            DropDownItemOptionComponent={TeamMemberDropdownItemOption}
          />
        </div>
        {selectedUserFilterOption ? (
          <PresetTemplateConfigForm
            preset={preset}
            inputParameters={inputParameters}
            defaultInputParameters={defaultInputParameters}
            parentInputParameters={parentInputParameters}
            editedInputParameters={editedInputParameters}
            showCustomizationSetting={showCustomizationSetting}
            customized={customized}
            shouldReset={shouldReset}
            shouldShowGenerate={showGenerate}
            showGenerate={() => setShowGenerate(true)}
            onChangeTemplate={handleChangeTemplate}
            onChangeExample={handleChangeExample}
            onChangeCustomized={handleChangeCustomized}
            onGenerate={setGenerating}
            disabled={!ipHierarchyEnabled || !hasIpHierarchyUpdateScope}
          />
        ) : null}
      </div>
      <div className='d-flex justify-content-between w-100 mt-3'>
        <Button variant='borderless' onClick={handleClickBack}>
          {showGenerate ? 'Back' : 'Close'}
        </Button>
        {hasIpHierarchyUpdateScope && (
          <div className='d-flex gap-2'>
            <>
              {ipHierarchyEnabled && (
                <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading || isGenerating}>
                  Cancel
                </Button>
              )}
              <ScopedBlock
                missingScopeAction={MissingScopeAction.UPGRADE}
                quota={{
                  quota: 'ai.ipHierarchy.value',
                }}
              >
                <Button variant='primary' onClick={handleClickSave} disabled={ipHierarchyEnabled && !isChanged} loading={updateLoading || isGenerating}>
                  {ipHierarchyEnabled ? 'Save' : 'Get feature'}
                </Button>
              </ScopedBlock>
            </>
          </div>
        )}
      </div>
    </>
  )
}
